import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from 'framer-motion'
import { DRAW_PER_TEAM_MS, type DrawState } from '@brochure/shared'
import { drawScene, videoAt, type DrawVideo } from '@/lib/draw'
import { cn } from '@/lib/utils'

const VIDEOS: readonly DrawVideo[] = ['intro', 'turn']
/** Ritardo tollerato prima di riallineare il video all'orologio: un riallineamento si vede, meglio pochi. */
const MAX_DRIFT_S = 0.75
/** Fondo dei video di Zambe: il riquadro ha lo stesso colore, così il bordo del video non si vede. */
const SCREEN = 'rgb(26 22 60)'
const TICK_MS = 100

type LiveDrawProps = {
  draw: DrawState
  participants: readonly { name: string }[]
  /** Rilegge il torneo: le coppie arrivano dal server una alla volta, all'inizio del loro turno. */
  onReload: () => void
}

/**
 * Estrazione live sulla pagina del torneo: Zambe (clip video) pesca le coppie dall'urna. Ogni telefono calcola il
 * momento dall'orologio del server (`serverNow`), quindi tutti vedono la stessa scena; i nomi vengono solo dalle
 * coppie già svelate dal server. Con "riduci movimento" niente video: fermi immagine e nomi.
 */
export function LiveDraw({ draw, participants, onReload }: LiveDrawProps) {
  const { t } = useTranslation()
  const isReduced = useReducedMotion() ?? false
  const offset = useMemo(() => Date.parse(draw.serverNow) - Date.now(), [draw.serverNow])
  const [now, setNow] = useState(() => Date.now())
  const videos = useRef<Partial<Record<DrawVideo, HTMLVideoElement | null>>>({})
  // Il video visibile cambia solo quando quello nuovo ha un fotogramma vero (`playing`/`seeked`): niente lampi.
  const [shown, setShown] = useState<DrawVideo>('intro')
  const elapsed = now + offset - Date.parse(draw.startedAt)
  const scene = drawScene(draw, participants, elapsed)
  const cue = videoAt(elapsed, draw.teamCount)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  // Una rilettura per svelamento (o subito, se questa risposta è rimasta indietro); a fine estrazione il server
  // risponde senza `draw` e la pagina passa alle tab del torneo.
  useEffect(() => {
    const id = setTimeout(onReload, scene.isBehind ? 400 : scene.nextRevealMs + 250)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una volta per risposta del server, non a ogni tick
  }, [draw.serverNow, scene.isBehind])

  // Suona solo il video del momento, allineato all'orologio del server; alla fine resta fermo sul pollice.
  const drift = Math.floor(cue.offsetMs / 500)
  useEffect(() => {
    if (isReduced) return
    const video = videos.current[cue.video]
    if (!video) return
    const target = cue.offsetMs / 1000
    const off = Math.abs(video.currentTime - target)
    // Il turno gira in loop: 14,9 s e 0,1 s sono vicini.
    const gap = cue.video === 'turn' ? Math.min(off, DRAW_PER_TEAM_MS / 1000 - off) : off
    if (cue.paused) {
      if (!video.paused) video.pause()
      if (gap > 0.1) video.currentTime = target
    } else {
      if (gap > MAX_DRIFT_S) video.currentTime = target
      if (video.paused) void video.play().catch(() => { /* autoplay negato: resta il poster */ })
    }
    if (shown === cue.video) videos.current[cue.video === 'intro' ? 'turn' : 'intro']?.pause()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `drift` ricontrolla l'allineamento ogni mezzo secondo
  }, [cue.video, cue.paused, drift, isReduced, shown])

  const still = scene.phase === 'approved' || scene.phase === 'outro' || scene.phase === 'done' ? '/media/draw/approved.webp' : '/media/draw/poster.webp'
  const progress = draw.teamCount === 0 ? 0 : (scene.pairs.length / draw.teamCount) * 100
  const bubble = t(`zambePong.draw.${scene.bubble.key}`, { name: scene.bubble.name })

  return (
    <section data-testid="live-draw" aria-labelledby="live-draw-title" className="overflow-hidden rounded-[2rem] text-paper shadow-lift" style={{ background: SCREEN }}>
      <div className="flex items-center justify-between gap-3 px-4 pt-4 sm:px-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-coral px-3 py-1 font-body text-xs font-extrabold text-white">
          <span className="size-1.5 animate-pulse rounded-full bg-white motion-reduce:animate-none" aria-hidden="true" />
          {t('zambePong.draw.live')}
        </span>
        {scene.teamNumber > 0 && (
          <span className="font-body text-sm font-bold text-paper/70 tabular-nums">{t('zambePong.draw.progress', { n: scene.teamNumber, total: draw.teamCount })}</span>
        )}
      </div>
      <h2 id="live-draw-title" className="px-4 pt-3 font-display text-3xl leading-none font-bold sm:px-6 sm:text-4xl">
        {t('zambePong.draw.title')}
      </h2>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-8 lg:px-6 lg:pb-6">
        <div className="mx-auto w-full max-w-[30rem]">
          {/* Fumetto sopra Zambe, mai sopra la sua testa: altezza fissa per due righe, così la scena non salta. */}
          <div className="flex min-h-[4.75rem] items-end justify-center px-4 pt-4">
            <p
              role="status"
              className="relative max-w-[90%] rounded-3xl bg-paper px-4 py-2 text-center font-display text-lg leading-tight font-semibold text-ink shadow-lift after:absolute after:-bottom-1.5 after:left-1/2 after:size-3.5 after:-translate-x-1/2 after:rotate-45 after:rounded-[3px] after:bg-paper sm:text-xl"
            >
              {bubble}
            </p>
          </div>
        <div className="relative aspect-square w-full">
          {isReduced ? (
            <img src={still} alt="" width={540} height={540} className="absolute inset-0 size-full" />
          ) : (
            VIDEOS.map((name) => (
              <video
                key={name}
                ref={(el) => {
                  videos.current[name] = el
                }}
                src={`/media/draw/${name}.mp4`}
                poster="/media/draw/poster.webp"
                muted
                playsInline
                preload="auto"
                loop={name === 'turn'}
                onPlaying={() => cue.video === name && setShown(name)}
                onSeeked={() => cue.video === name && setShown(name)}
                aria-hidden="true"
                className={cn('absolute inset-0 size-full', name === shown ? 'opacity-100' : 'opacity-0')}
              />
            ))
          )}
        </div>
        </div>

        <div className="px-4 pb-5 sm:px-6 lg:px-0 lg:pt-6">
          <div className="relative -mt-3 rounded-3xl bg-paper p-4 text-ink shadow-lift lg:mt-0">
            {scene.stamped && (
              <span className="absolute -top-3.5 right-3 rotate-6 rounded-xl border-[3px] border-coral bg-paper px-2.5 py-0.5 font-display text-sm font-bold text-coral motion-safe:animate-[stamp_400ms_cubic-bezier(.5,0,.75,0)_both]">
                {t('zambePong.draw.stamp')} ✓
              </span>
            )}
            <p className="font-display text-lg font-bold">
              {scene.teamNumber > 0 ? t('zambePong.draw.ticket', { n: scene.teamNumber }) : t('zambePong.draw.waiting')}
            </p>
            <div className="ticket-perforation my-3" />
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              {scene.names.map((name, i) => (
                <div key={i} className={cn('grid min-h-14 place-items-center overflow-hidden rounded-2xl bg-paper-deep px-2 text-center font-display text-xl font-bold', i === 1 && 'col-start-3')}>
                  {name ? (
                    <span key={name} className="motion-safe:animate-flap">
                      {name}
                    </span>
                  ) : (
                    <span className="text-2xl text-ink/25" aria-hidden="true">
                      ?
                    </span>
                  )}
                </div>
              ))}
              <span className="col-start-2 row-start-1 font-display text-xl font-bold text-coral" aria-hidden="true">
                +
              </span>
            </div>
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-paper-deep" aria-hidden="true">
              <div className="h-full rounded-full bg-coral transition-[width] duration-500 ease-out-expo" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {scene.pairs.length > 0 && (
            <>
              <h3 className="mt-6 font-display text-lg font-semibold text-paper/75">{t('zambePong.draw.pairs')}</h3>
              <ol className="mt-2 grid gap-2">
                {scene.pairs.map((pair) => (
                  <li key={pair.number} className="flex items-center gap-3 rounded-2xl border border-paper/12 bg-paper/8 px-3 py-2.5 font-body font-bold">
                    <span className="grid size-7 shrink-0 place-items-center rounded-full bg-sea font-display text-sm" aria-hidden="true">
                      {pair.number}
                    </span>
                    {pair.names[0]} + {pair.names[1]}
                  </li>
                ))}
              </ol>
            </>
          )}

          {scene.urn.length > 0 && (
            <>
              <h3 className="mt-6 font-display text-lg font-semibold text-paper/75">{t('zambePong.draw.urn')}</h3>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {scene.urn.map((name) => (
                  <li key={name} className="rounded-full bg-sun/20 px-3 py-1.5 font-body text-sm font-bold text-sun">
                    {name}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="mt-6 font-body text-sm text-paper/60">{t('zambePong.draw.hint')}</p>
        </div>
      </div>
    </section>
  )
}
